import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import {
  Button,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  LabraToolTipV2,
  PlanType,
  StagesChips,
  StatusChipV4,
  TableComponentV2,
} from '@labrav/react-components'
import { useNavigate, useParams } from 'react-router-dom'
import {
  actionTypeWrapper,
  type CloudAccountChangeParams,
  type CloudType,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  getSinceDateDay,
  isLoading,
  LoadingTypes,
  PartnerType,
  setSelectedAccount,
  ShortenedNameV2,
  startLoading,
  stopLoading,
  useAccountId,
  useChatContext,
  useCloudAccounts,
  useCurrentCloudProvider,
  useScreenSize,
  useSyncCloudMarketplace,
  useUserType,
  useValidateCloudRoute,
} from '@labrav/common'
import { useDispatch, useSelector } from '../../store/hooks'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import { useCommonStyles } from '../../common/styles/common.styles'
import { useStyles } from './ProductListingV2.styles'
import { GoLiveButton } from '../../common/components/GoLiveButton/GoLiveButton'
import AddListingModal from '../../common/components/AddListingModal/AddListingModal'
import { AddProductModal } from '../../common/components/AddProductModal/AddProductModal'
import { ListingCreationPage } from '../../common/components/ListingCreationPage/ListingCreationPage'
import { MigrationInProgressBanner } from '../../common/components/MigrationInProgressBanner/MigrationInProgressBanner'
import { ResponsiveColumn } from '../../common/components/ResponsiveColumn/ResponsiveColumn'
import { getTextForProduct } from '../../common/utils/getTextForProduct'
import { getStatusAndLabelForProduct } from '../../common/utils/getStatusAndLabelForProduct'
import {
  clearProductsListing,
  deleteProductListingData,
  getProducts,
  getProductsV2,
  publishProductListingData,
  setSelectedProductOnListing,
  unpublishProductListingData,
} from '../../modules/productsListing/action'
import {
  FreeTrialStatus,
  Product,
  ProductStatus,
} from '../../modules/productsListing/types'
import { setCloudMarketplaceProductListing } from '../../modules/productListingFilter/action'
import { EnableFreeTrial } from '../EnableFreeTrial/EnableFreeTrial'
import { ViewFreeTrial } from '../ViewFreeTrial/ViewFreeTrial'
import { NoOfActiveContracts } from './NoOfActiveContracts/NoOfActiveContracts'
import { ProductListingToolbar } from './ProductListingToolbar'
import { ProductListingConfirmModals } from './ProductListingConfirmModals'
import {
  EMPTY_SELECTED_PRODUCT,
  ExternalPlatformType,
  type ProductListingV2Props,
  type SelectedProduct,
} from './types'
import {
  getEditViewProductRedirectUrl,
  getListingMutationAlerts,
  getMenuOptions,
  getMigrateExistingListingPath,
  getProductListingCloudChangePath,
  getProductListingTableMapping,
} from './helpers'

export {
  mapping,
  mappingAdminUi,
  getFreeTrialTextWithDisable,
  getPubishTextWithDisable,
  getFreeTrialTextForProduct,
  getPublishTextForProduct,
  isProductMeteringDisabled,
  getEditViewProductRedirectUrl,
  getMenuOptions,
} from './helpers'
export { ExternalPlatformType } from './types'
export { CreateNewListingButton } from './CreateNewListingButton'

export const ProductListingV2: React.FC<ProductListingV2Props> = ({
  externalBaseURL,
  externalPlatformType,
  search,
}) => {
  const isHubspot = externalPlatformType === ExternalPlatformType.HUBSPOT
  const isImpartner = externalPlatformType === ExternalPlatformType.IMPARTNER

  const { classes } = useStyles({
    isExternal: !!externalBaseURL || isHubspot,
    externalPlatformType,
  })
  const { classes: commonClasses } = useCommonStyles()
  const [openUnpublishModal, setOpenUnpublishModal] = useState(false)
  const [openPublishModal, setOpenPublishModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openFreeTrialModal, setOpenFreeTrialModal] = useState(false)
  const [openViewFreeTrialModal, setOpenViewFreeTrialModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct>(
    EMPTY_SELECTED_PRODUCT
  )
  const [openAddProductModal, setOpenAddProductModal] = useState(false)
  const [modalProductData, setModalProductData] = useState<Product | null>(null)

  const dispatch = useDispatch()
  useSyncFlyoutRouteParams(!!externalBaseURL)
  useSyncCloudMarketplace()

  const {
    productsV2: productRows,
    additionalAllowedProducts,
    plan,
  } = useFlyOutProductsBasedOnSelectedCloud()
  const productRowsList = Array.isArray(productRows) ? productRows : []
  const tableEl = useRef<HTMLDivElement>(null)
  const accountId = useAccountId({ product: 'flyout' })
  const navigate = useNavigate()
  const { screenWidth, pageSize: pageSizeValue } = useScreenSize()
  const columnMaxWidth = useMemo(
    () => `${(screenWidth * 30) / 100}px`,
    [screenWidth]
  )
  const isAdmin = useUserType() === PartnerType.Admin
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const migrationQueueProduct = productRowsList.find(
    item => item?.status === ProductStatus.PRODUCT_STATUS_MIGRATION_IN_QUEUE
  )
  const isProductInMigrationInQueue =
    (selectedCloudTypeProductListing === 'AZURE' ||
      selectedCloudTypeProductListing === 'AWS') &&
    Boolean(migrationQueueProduct)
  const migrateListingPath = getMigrateExistingListingPath(
    selectedCloudTypeProductListing === 'AWS'
      ? migrationQueueProduct?.productId
      : undefined
  )
  const migrationResumePath =
    selectedCloudTypeProductListing === 'AWS' &&
    migrationQueueProduct?.productId
      ? migrateListingPath
      : undefined

  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    secureKey: secureKeyFromURL,
    partnerId: partnerIdFromURL,
  } = useParams<{
    cloud: string
    accountId: string
    secureKey: string
    partnerId: string
  }>()
  const marketplaceCloudAccounts = useCloudAccounts('flyout')

  useValidateCloudRoute('flyout', '/flyout', !!externalBaseURL)

  const loading = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS_V2)
  )
  const { pageNumber, totalCount, hasMore } = useSelector(state => {
    const productsV2 =
      state.productsListing[selectedCloudTypeProductListing]?.productsV2
    return {
      pageNumber: productsV2?.pageNumber ?? 1,
      totalCount: productsV2?.totalCount ?? 0,
      hasMore: Boolean(productsV2?.hasMore),
    }
  })

  // Tell the chat which listings are on screen. Counts and the cloud only —
  // the rows stay in the table, and the agent looks them up itself.
  useChatContext('productListings', () => ({
    cloud: selectedCloudTypeProductListing,
    search,
    visible: { count: totalCount, page: pageNumber },
  }))

  const currentAccountId = routeAccountId || accountId || ''
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )

  const refreshProducts = useCallback(() => {
    dispatch(getProducts(currentAccountId, selectedCloudTypeProductListing))
    dispatch(
      getProductsV2(
        currentAccountId,
        selectedCloudTypeProductListing,
        true,
        false,
        {
          pageNumber: 1,
          pageSize: pageSizeValue,
        }
      )
    )
  }, [
    currentAccountId,
    dispatch,
    pageSizeValue,
    selectedCloudTypeProductListing,
  ])

  const resetSelectedProduct = useCallback(() => {
    setSelectedProduct(EMPTY_SELECTED_PRODUCT)
  }, [])

  const deleteProduct = async (product: SelectedProduct) => {
    await dispatch(
      deleteProductListingData({
        partnerId: currentAccountId,
        productId: product?.productId,
        alertMessages: product?.alertMessages,
      })
    )
    refreshProducts()
    setOpenDeleteModal(false)
    resetSelectedProduct()
  }

  const unpublish = async (product: SelectedProduct) => {
    await dispatch(
      unpublishProductListingData({
        partnerId: currentAccountId,
        productId: product?.productId,
        alertMessages: product?.alertMessages,
      })
    )
    refreshProducts()
    setOpenUnpublishModal(false)
    resetSelectedProduct()
  }

  const publish = async (product: SelectedProduct) => {
    await dispatch(
      publishProductListingData({
        partnerId: currentAccountId,
        productId: product?.productId,
        alertMessages: product?.alertMessages,
      })
    )
    refreshProducts()
    setOpenUnpublishModal(false)
    resetSelectedProduct()
  }

  const successFreeTrial = () => {
    refreshProducts()
    setOpenFreeTrialModal(false)
    resetSelectedProduct()
  }

  const successDeactivateFreeTrial = () => {
    refreshProducts()
    setOpenViewFreeTrialModal(false)
    resetSelectedProduct()
  }

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params
      const pathname = getProductListingCloudChangePath({
        cloud,
        originalAccountId,
        isHubspot,
        isImpartner,
        secureKeyFromURL,
        partnerIdFromURL,
        search,
      })
      navigate(pathname, { replace: true })

      await dispatch(startLoading(LoadingTypes.GENERAL))
      await dispatch(setSelectedAccount(originalAccountId, 'flyout', cloud))
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(setCloudMarketplaceProductListing(cloud))
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [
      dispatch,
      navigate,
      selectedCloudTypeProductListing,
      isImpartner,
      isHubspot,
      secureKeyFromURL,
      partnerIdFromURL,
      search,
    ]
  )

  useEffect(() => {
    if (!externalBaseURL && accountId && !routeAccountId && !isImpartner) {
      const defaultAccount = marketplaceCloudAccounts.find(
        account => account.originalId === accountId
      )
      if (defaultAccount) {
        const cloudPath = defaultAccount.provider.toLowerCase()
        const cloudUpper = defaultAccount.provider.toUpperCase() as CloudType
        dispatch(setSelectedAccount(accountId, 'flyout', cloudUpper))
        navigate(`/flyout/${cloudPath}/${accountId}/products-listing`, {
          replace: true,
        })
      }
    }
  }, [
    accountId,
    marketplaceCloudAccounts,
    navigate,
    externalBaseURL,
    isImpartner,
    dispatch,
    routeAccountId,
  ])

  useEffect(() => {
    if (currentAccountId && routeAccountId) {
      const routeCloudUpper = routeCloud?.toUpperCase()
      const isCloudSynced =
        !routeCloudUpper || routeCloudUpper === selectedCloudTypeProductListing

      if (!externalBaseURL && isCloudSynced) {
        dispatch(
          actionTypeWrapper(
            selectedCloudTypeProductListing,
            clearProductsListing()
          )
        )
        dispatch(
          getProductsV2(
            currentAccountId,
            selectedCloudTypeProductListing,
            true,
            false,
            {
              pageNumber: 1,
              pageSize: pageSizeValue,
            }
          )
        )
        dispatch(getProducts(currentAccountId, selectedCloudTypeProductListing))
      }
    }
  }, [
    currentAccountId,
    selectedCloudTypeProductListing,
    routeAccountId,
    routeCloud,
    externalBaseURL,
    dispatch,
    pageSizeValue,
  ])

  const handleCreateListingSelection = (option: ColumnMenuOption) => {
    if (option.key === 'create-new-listing') {
      setOpenAddProductModal(true)
    } else {
      navigate(migrateListingPath)
    }
  }

  const handleSelectOption =
    (productId: string) => async (option: ColumnMenuOption) => {
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(productId)
        )
      )
      if (option.route) {
        navigate(option?.route)
      } else if (option?.key === 'delete-product') {
        setOpenDeleteModal(true)
        setSelectedProduct({
          productId: option?.params?.productId,
          alertMessages: getListingMutationAlerts({
            productName: option?.params?.productName,
            productStatus: option?.params?.productStatus,
            action: 'deleted',
          }),
        })
      } else if (option?.key === 'publish-product') {
        setOpenPublishModal(true)
        setSelectedProduct({
          productId: option?.params?.productId,
          alertMessages: getListingMutationAlerts({
            productName: option?.params?.productName,
            productStatus: option?.params?.productStatus,
            action: 'published',
          }),
        })
      } else if (option?.key === 'unpublish-product') {
        setOpenUnpublishModal(true)
        setSelectedProduct({
          productId: option?.params?.productId,
          alertMessages: getListingMutationAlerts({
            productName: option?.params?.productName,
            productStatus: option?.params?.productStatus,
            action: 'unpublished',
          }),
        })
      } else if (option?.key === 'view-free-trial') {
        await setSelectedProduct({
          productId: option?.params?.productId,
        })
        if (option?.params?.isView) {
          await setOpenViewFreeTrialModal(true)
        } else {
          await setOpenFreeTrialModal(true)
        }
      }
    }

  const customObject = productRows.map(productRow => {
    const statusDetails = getStatusAndLabelForProduct(productRow.status)
    const baseObject: Record<
      string,
      string | boolean | React.ReactElement | null
    > = {
      id: productRow?.productId,
      sellerProductName: (
        <ResponsiveColumn
          linkText={productRow?.productName}
          linkUrl={productRow?.productUrl}
          maxWidth={columnMaxWidth}
          otherElement={
            productRow?.freeTrial?.freeTrialStatus ===
            FreeTrialStatus.FREE_TRIAL_CREATED ? (
              <StagesChips label="Free trial" status="success" />
            ) : (
              <></>
            )
          }
        />
      ),
      status: isEmpty(productRow.status) ? (
        '-'
      ) : (
        <LabraToolTipV2
          tooltipPlacement="bottom"
          description={getSinceDateDay(productRow.updatedAt)}
        >
          <StatusChipV4 {...statusDetails} />
        </LabraToolTipV2>
      ),
      type: getTextForProduct(
        productRow?.productType || ('' as PlanType),
        selectedCloudTypeProductListing
      ),
      noOfActiveContracts: (
        <NoOfActiveContracts
          productRow={productRow}
          cloudType={selectedCloudTypeProductListing}
          hideContractLink={!!externalBaseURL}
        />
      ),
      action:
        ((productRow?.productType === PlanType.PROFESSIONAL_SERVICES ||
          productRow?.productType === PlanType.CONTAINER) &&
          productRow?.status ===
            ProductStatus.PRODUCT_STATUS_CREATION_FAILED) ||
        isImpartner ? (
          <></>
        ) : (
          <div
            className={commonClasses.actionColumn}
            data-prevent-row-click
            data-testid={'action'}
          >
            <ColumnActionWithMenuV2
              handleSelectOption={handleSelectOption(productRow?.productId)}
              menuOptions={getMenuOptions({
                productRow,
                cloudProvider: selectedCloudTypeProductListing,
                externalBaseURL,
                search,
                externalPlatformType,
                cloud: routeCloud,
                accountId: currentAccountId,
              })}
            />
          </div>
        ),
    }
    if (!isImpartner) {
      baseObject.noOfActiveContracts = (
        <NoOfActiveContracts
          productRow={productRow}
          cloudType={selectedCloudTypeProductListing}
          hideContractLink={!!externalBaseURL || isHubspot}
        />
      )
    }

    return baseObject
  })

  const customObjectAdmin = productRows.map(productRow => {
    const statusDetails = getStatusAndLabelForProduct(productRow.status)
    return {
      sellerProductName: '',
      sellerProductId: (
        <ShortenedNameV2 name={productRow.productId} length={16} />
      ),
      status: <StatusChipV4 {...statusDetails} />,
      noOfContracts: productRow.numberOfContracts
        ? productRow.numberOfContracts.toString()
        : '-',
      actions: (
        <div className={classes.buttonContainer}>
          <GoLiveButton row={productRow} partnerId={currentAccountId} />
          <Button
            className={classes.submitButton}
            onClick={() => setModalProductData(productRow)}
            data-testid="add-listing-button"
          >
            Add listing details
          </Button>
        </div>
      ),
    }
  })

  const fetchMoreData = useCallback(() => {
    if (
      externalBaseURL ||
      isHubspot ||
      !currentAccountId ||
      !hasMore ||
      loading
    ) {
      return
    }
    dispatch(
      getProductsV2(
        currentAccountId,
        selectedCloudTypeProductListing,
        true,
        false,
        {
          pageNumber: pageNumber + 1,
          pageSize: pageSizeValue,
        }
      )
    )
  }, [
    currentAccountId,
    dispatch,
    externalBaseURL,
    hasMore,
    isHubspot,
    loading,
    pageNumber,
    pageSizeValue,
    selectedCloudTypeProductListing,
  ])

  const handleClickRow = (selectedId: string) => {
    const clickedProduct = productRows.find(
      item => item.productId === selectedId
    )
    if (!isImpartner && clickedProduct) {
      const shouldRedirect =
        clickedProduct.productType &&
        [PlanType.SAAS_CONTRACT, PlanType.SAAS_SUBSCRIPTION].includes(
          clickedProduct.productType
        ) &&
        clickedProduct?.status &&
        ![
          ProductStatus.PRODUCT_STATUS_CREATION_FAILED,
          ProductStatus.PRODUCT_STATUS_IN_PROGRESS,
          ProductStatus.MIGRATION_IN_PROGRESS,
        ].includes(clickedProduct?.status)

      const redirectURL = getEditViewProductRedirectUrl(
        clickedProduct,
        routeCloud,
        currentAccountId
      )
      if (shouldRedirect && !externalBaseURL && !isHubspot) {
        navigate(redirectURL)
      }
    }
  }

  const isFreeTier = plan === 'free_tier'
  const enableToCreate = isFreeTier
    ? additionalAllowedProducts !== undefined && additionalAllowedProducts > 0
    : true
  const objectLabraUI = isAdmin ? customObjectAdmin : customObject
  const showEmptyListingPage =
    productRows &&
    !productRows?.length &&
    !loading &&
    !externalBaseURL &&
    !isHubspot &&
    !isImpartner

  return (
    <>
      {showEmptyListingPage ? (
        <ListingCreationPage />
      ) : (
        <div
          data-testid="product-listing"
          className={clsx(
            isAdmin ? classes.containerAdmin : classes.container,
            externalBaseURL || isHubspot ? classes.scroll : '',
            classes.root
          )}
          {...(externalBaseURL || isHubspot ? { ref: tableEl } : {})}
        >
          {isProductInMigrationInQueue && (
            <MigrationInProgressBanner resumePath={migrationResumePath} />
          )}
          <div className={classes.productListing}>
            <div className={classes.dropdownAndProduct}>
              <ProductListingToolbar
                isHubspot={isHubspot}
                isImpartner={isImpartner}
                externalBaseURL={externalBaseURL}
                marketplaceCloudAccounts={marketplaceCloudAccounts}
                selectedAccountIdWithCloud={selectedAccountIdWithCloud}
                onCloudAccountChange={handleCloudAccountChange}
                isFreeTier={isFreeTier}
                enableToCreate={enableToCreate}
                loading={loading}
                handleCreateListingSelection={handleCreateListingSelection}
                handleMigrateListingSelection={() =>
                  navigate(migrateListingPath)
                }
                selectedCloudTypeProductListing={
                  selectedCloudTypeProductListing
                }
                externalPlatformType={externalPlatformType}
                productRows={productRows}
              />
            </div>
            <div>
              {isImpartner && !productRows?.length && !loading ? (
                <div className={classes.noProductsContainer}>
                  No products added.
                </div>
              ) : (
                <TableComponentV2
                  mapping={getProductListingTableMapping({
                    isAdmin,
                    isImpartner,
                  })}
                  objects={objectLabraUI}
                  keyName={'sellerProduct'}
                  count={totalCount || productRows?.length || 0}
                  hasMoreData={hasMore}
                  fetchMoreData={fetchMoreData}
                  selectedItem={[]}
                  isLoading={loading}
                  onChangeCheckbox={() => void 0}
                  showCheckbox={false}
                  breakWorkHeader={false}
                  rightAlignColumns={isImpartner ? [] : ['noOfActiveContracts']}
                  tableHeight={'calc(100vh - 190px)'}
                  onChangeRow={handleClickRow}
                  customColumns={['action']}
                  stickyColumns={['action']}
                  customCellProps={[{ style: { width: columnMaxWidth } }]}
                  {...(externalBaseURL || isHubspot
                    ? { outerRef: tableEl }
                    : {})}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <AddProductModal
        openAddProductModal={openAddProductModal}
        handleCloseAddProductModal={() => setOpenAddProductModal(false)}
      />
      {modalProductData ? (
        <AddListingModal
          modalProductData={modalProductData}
          handleClose={() => setModalProductData(null)}
          accountId={currentAccountId}
        />
      ) : null}
      <ProductListingConfirmModals
        openUnpublishModal={openUnpublishModal}
        openDeleteModal={openDeleteModal}
        openPublishModal={openPublishModal}
        onUnpublish={() => unpublish(selectedProduct)}
        onDelete={() => deleteProduct(selectedProduct)}
        onPublish={() => publish(selectedProduct)}
        onUnpublishDecline={() => setOpenUnpublishModal(false)}
        onDeleteDecline={() => setOpenDeleteModal(false)}
        onPublishDecline={() => setOpenPublishModal(false)}
      />
      {selectedProduct?.productId && (
        <EnableFreeTrial
          successFreeTrial={successFreeTrial}
          selectedProductId={selectedProduct?.productId}
          openFreeTrialModal={openFreeTrialModal}
          setOpenFreeTrialModal={setOpenFreeTrialModal}
        />
      )}
      {selectedProduct?.productId && (
        <ViewFreeTrial
          successDeactivateFreeTrial={successDeactivateFreeTrial}
          selectedProductId={selectedProduct?.productId}
          openViewFreeTrialModal={openViewFreeTrialModal}
          setOpenViewFreeTrialModal={setOpenViewFreeTrialModal}
        />
      )}
    </>
  )
}
